import React, { useState } from 'react';
import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';
import PropTypes from 'prop-types';

import Dialog from 'epn-ui/umd/dist/components/Dialog';
import useI18n from '@i18n';
import { companyName } from 'src/config/globalDomain';
import apiHooks, { ICreateUseRequestResponse } from 'src/hooks/api';
import api from 'src/store/api';

import { IGetBotUsersDataFormatted } from '@api/types';
import Table from './Table';

const { useBotUsers } = apiHooks;

const ROLE_HEAD = 'head';
const ROLE_USER = 'user';

const CHANGE_BOT_TYPES = { delete: 'delete', update: 'update' };

interface IMyAccountsBlock {
    auth?: boolean;
    goAuth: () => void;
}

const MyAccountsBlock: React.FC<IMyAccountsBlock> = ({ auth = false, goAuth }) => {
    const { t } = useI18n('TelegramBot');
    const [modal, showModal] = useState<boolean>(false);

    const { data: botUsersData = [], isLoading, mutate: refreshBotUsers } = useBotUsers({
        condition: modal,
    }) as ICreateUseRequestResponse<IGetBotUsersDataFormatted[]>;

    const myUsers = botUsersData.filter(e => e.role === ROLE_HEAD) || [];
    const otherUsers = botUsersData.filter(e => e.role === ROLE_USER) || [];

    const onShowModal = (): void => {
        if (!auth) {
            goAuth();
            return;
        }

        showModal(true);
    };

    const onCloseModal = (): void => {
        showModal(false);
    };

    const changeBotUsersList = async (
        type: string,
        user: IGetBotUsersDataFormatted,
    ): Promise<void> => {
        const changeBotConfig = {
            [CHANGE_BOT_TYPES.delete]: {
                changeFunc: data => data.filter(el => el.id !== user.id),
                method: api.bot.deleteUser,
                params: { urlParams: { id: user.id } },
            },
            [CHANGE_BOT_TYPES.update]: {
                changeFunc: data => data.map(el => (el.id === user.id ? user : el)),
                method: api.bot.updateUser,
                params: { urlParams: { id: user.id }, body: user },
            },
        };

        const { changeFunc, method, params } = changeBotConfig[type];

        try {
            refreshBotUsers(changeFunc, false);

            await method(params);

            refreshBotUsers();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center w-full bg-[#53acd410] py-[50px] mb-[100px]">
            <p className="mx-0 text-base text-center min-w-[100px] text-brandBlue-300 sm:w-[700px] mb-[24px]">
                {t('All accounts connected to you will be displayed below')}
            </p>
            <Button type="primary" size="middle" onClick={onShowModal}>
                {t('My accounts')}
            </Button>
            {modal && (
                <>
                    <Dialog
                        title={<p className="text-center">{t('My Telegram Accounts')}</p>}
                        onCancel={onCloseModal}
                        open
                        size="lg"
                    >
                        <div id="modal-my-telegram-accounts">
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    <h4 className="font-medium text-center text-[16px] mb-[16px] text-brandBlue-500 mex-w-[700px]">
                                        {t('Affiliate account')}
                                    </h4>
                                    <p className="mx-0 text-[16px] text-brandBlue-300 sm:w-[700px]">
                                        {t('AboutWebmasterBotAccount', { companyName })}
                                    </p>
                                    <p className="mx-0 text-[16px] mt-[8px] text-brandBlue-300 mb-[24px] sm:w-[700px]">
                                        {t('AboutWebmasterBotAccountFeature')}
                                    </p>
                                    {!!myUsers.length && (
                                        <Table
                                            users={myUsers}
                                            deleteUser={user =>
                                                changeBotUsersList(CHANGE_BOT_TYPES.delete, user)
                                            }
                                            updateUser={user =>
                                                changeBotUsersList(CHANGE_BOT_TYPES.update, user)
                                            }
                                            actionTooltipId={t('WhenDeleteAccount', {
                                                companyName,
                                            })}
                                        />
                                    )}
                                    <h4 className="font-medium text-center text-[16px] mt-[32px] mb-[16px] text-brandBlue-500 mex-w-[700px]">
                                        {t("My assistants' accounts")}
                                    </h4>
                                    <p className="mx-0 text-[16px] text-brandBlue-300 mb-[24px] sm:w-[700px]">
                                        {t('AboutMyAssistantsBotAccount')}
                                    </p>
                                    {!!otherUsers.length && (
                                        <Table
                                            users={otherUsers}
                                            deleteUser={user =>
                                                changeBotUsersList(CHANGE_BOT_TYPES.delete, user)
                                            }
                                            updateUser={user =>
                                                changeBotUsersList(CHANGE_BOT_TYPES.update, user)
                                            }
                                            actionTooltipId={t('WhenDeleteAssistant')}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </Dialog>
                </>
            )}
        </div>
    );
};

MyAccountsBlock.defaultProps = {
    auth: false,
};

MyAccountsBlock.propTypes = {
    auth: PropTypes.bool,
    goAuth: PropTypes.func.isRequired,
};

export default MyAccountsBlock;
